import { useCallback, useState } from 'react';
import fileApiService from 'src/services/file-api-service';
import { useSiteFilesQuery } from 'src/app/projects/views/site-files/state/api/siteFilesGraphSlice';
import type { ProjectFile } from 'src/shared/types/graph/project';

type UseFilesScope = {
    readonly projectId: string | undefined;
};

type UploadResult = {
    isSuccess: boolean;
    message: string;
};

type UseFilesReturn = {
    files: ProjectFile[];
    isLoading: boolean;
    isRefetching: boolean;
    error: unknown | null;
    refresh: () => Promise<any>;

    // Mutations
    upload: (files: File[]) => Promise<UploadResult>;
    rename: (fileId: string, newName: string) => Promise<void>;
    removeOne: (fileId: string) => Promise<void>;
    downloadOne: (fileId: string) => Promise<void>;
    downloadMultipleFiles: (fileIds: string[]) => Promise<void>;

    // Mutation states
    isUploading: boolean;
    isRenaming: boolean;
    isDeleting: boolean;
    isDownloading: boolean;
};

export function useFiles({ projectId }: UseFilesScope): UseFilesReturn {
    const {
        currentData: files = [],
        isFetching,
        isLoading: isInitialLoading,
        refetch,
        error,
    } = useSiteFilesQuery({ projectId: projectId || '' }, { skip: !projectId });

    const [isUploading, setIsUploading] = useState(false);
    const [isRenaming, setIsRenaming] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const refresh = useCallback(async () => {
        return await refetch();
    }, [refetch]);

    const upload = useCallback(
        async (filesToUpload: File[]): Promise<UploadResult> => {
            if (!filesToUpload || filesToUpload.length === 0) {
                return {
                    isSuccess: false,
                    message: 'No files to upload',
                };
            }

            if (!projectId) {
                return {
                    isSuccess: false,
                    message: 'Missing scope: projectId is required',
                };
            }

            setIsUploading(true);
            try {
                for (const file of filesToUpload) {
                    await fileApiService.uploadFile({ projectId, file });
                }

                await refresh();

                return {
                    isSuccess: true,
                    message: `${filesToUpload.length} ${filesToUpload.length === 1 ? 'file' : 'files'} uploaded successfully`,
                };
            } catch (e) {
                const message = (e as Error)?.message || 'Failed to upload files';
                return {
                    isSuccess: false,
                    message,
                };
            } finally {
                setIsUploading(false);
            }
        },
        [refresh, projectId]
    );

    const rename = useCallback(
        async (fileId: string, newName: string) => {
            if (!fileId) {
                return;
            }

            setIsRenaming(true);
            try {
                await fileApiService.renameFile({ id: Number(fileId), newName });
                await refresh();
            } finally {
                setIsRenaming(false);
            }
        },
        [refresh]
    );

    const removeOne = useCallback(
        async (fileId: string) => {
            if (!fileId) {
                return;
            }

            setIsDeleting(true);
            try {
                await fileApiService.deleteFiles({ ids: [Number(fileId)] });
                await refresh();
            } finally {
                setIsDeleting(false);
            }
        },
        [refresh]
    );

    const downloadMultipleFiles = useCallback(async (fileIds: string[]) => {
        if (!fileIds || fileIds.length === 0) {
            return;
        }

        setIsDownloading(true);
        try {
            const numericIds = fileIds.map(id => Number(id)).filter(n => !Number.isNaN(n));
            if (numericIds.length === 0) {
                return;
            }

            const { blob, filename } = await fileApiService.downloadFiles({ ids: numericIds });

            const a = document.createElement('a');
            const url = URL.createObjectURL(blob);
            a.href = url;
            a.download = numericIds.length > 1 ? 'files.zip' : filename || 'file.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setIsDownloading(false);
        }
    }, []);

    const downloadOne = useCallback(
        async (fileId: string) => {
            if (!fileId) {
                return;
            }

            await downloadMultipleFiles([fileId]);
        },
        [downloadMultipleFiles]
    );

    return {
        files,
        isLoading: Boolean(isInitialLoading),
        isRefetching: Boolean(isFetching),
        error: error ?? null,
        refresh,
        upload,
        rename,
        removeOne,
        downloadOne,
        downloadMultipleFiles,
        isUploading,
        isRenaming,
        isDeleting,
        isDownloading,
    };
}

export default useFiles;
